import React, { useState, useCallback } from 'react';

import { useIntl } from 'ha/i18n';
import { SortingValue } from 'ha/types/SearchFilters';
import Button, { ButtonProps } from 'ha/ui/next/Button';
import Menu from 'ha/ui/next/Menu';
import MenuItem from 'ha/ui/next/MenuItem';
import { Typography } from 'ha/ui/next/Typography';

import ArrowDownIcon from 'streamline-icons/arrow-button-down-bold.svg';

import useSorting from '../hooks/useSorting';

interface Props {
  size?: ButtonProps['size'];
}

const SortingButton: React.FC<Props> = ({ size }) => {
  const { T } = useIntl();
  const { currentSorting, onChange, onOpen, options } = useSorting();

  const [anchorEl, setAnchorEl] = useState<Element | undefined>(undefined);
  const [isPristine, setPristine] = useState(
    currentSorting === SortingValue.BestMatch,
  );

  const handleOpen = useCallback((event: React.MouseEvent) => {
    onOpen();
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(undefined);
  }, []);

  const handleMenuItemClick = useCallback((value: SortingValue) => {
    setPristine(false);
    onChange(value);
    setAnchorEl(undefined);
  }, []);

  return (
    <React.Fragment>
      <Button
        variant={isPristine ? 'outlined' : 'contained'}
        size={size}
        color="primary"
        onClick={handleOpen}
        iconSize="inherit"
        endIcon={<ArrowDownIcon />}
      >
        {isPristine
          ? T('Sort by')
          : options.find(option => option.value === currentSorting)?.key}
      </Button>
      <Menu
        id="sorting-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem dense disabled>
          <Typography.Caption textTransform="uppercase">
            {T('Sort by')}
          </Typography.Caption>
        </MenuItem>
        {options.map(option => (
          <MenuItem
            dense
            key={option.key}
            selected={option.value === currentSorting}
            onClick={() => handleMenuItemClick(option.value)}
          >
            {option.key}
          </MenuItem>
        ))}
      </Menu>
    </React.Fragment>
  );
};

export default SortingButton;
